import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, once } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { isAlive, uniqueId } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';

import { themeClassNames } from 'webapp/models-custom/theme';

export interface Args {
	value: string;
	onChange: (value: string) => void;
	searchDelay?: number;
	maxLength?: number;
	placeholder?: string;
	autofocus?: boolean;
	autosearch?: boolean;
	size?: string;
	theme?: string;
	onIsActiveChange?: (value: boolean) => void;
}

export default class FormInputSearchComponent extends Component<Args> {
	@tracked typedValue = '';
	@tracked isFocused = false;

	name = `input-search-${uniqueId()}`;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.typedValue = this.args.value || '';
	}

	get themeClass() {
		return themeClassNames(this.args.theme);
	}

	get sizeClass() {
		let sizeClass = '';
		if (this.args.size) {
			sizeClass = `size-${this.args.size}`;
		}
		return sizeClass;
	}

	get isActive() {
		return this.isFocused || !!this.typedValue;
	}

	get isClearTextBtnShown() {
		return !!this.typedValue;
	}

	get autosearch() {
		return this.args.autosearch ?? true;
	}

	get searchDelay() {
		return this.args.searchDelay ?? 400;
	}

	get placeholder() {
		return this.args.placeholder ?? 'Search...';
	}

	get maxLength() {
		return this.args.maxLength ?? 256;
	}

	@action
	focusSearch() {
		this.isFocused = true;
		this.scheduleCheckIsActive();
	}

	@action
	unfocusSearch() {
		this.isFocused = false;
		this.scheduleCheckIsActive();
		this.onEnterPress();
	}

	@action
	onEnterPress() {
		if (!this.autosearch) {
			this.triggerChange();
		}
	}

	@action
	clearText() {
		this.typedValue = '';
		this.triggerChange();
	}

	@action
	checkIsActive() {
		const onIsActiveChange = this.args.onIsActiveChange;
		if (onIsActiveChange) {
			onIsActiveChange(this.isActive);
		}
	}

	@action
	onInput() {
		this.checkIsActive();

		if (!this.autosearch) {
			return;
		}

		if (this.typedValue) {
			debounce(this, this.triggerChange, this.searchDelay);
		} else {
			this.triggerChange();
		}
	}

	@action
	onValueChange() {
		// if changed from outside (e.g. search reset)
		const newValue = this.args.value?.toLowerCase(),
			oldValue = this.typedValue?.toLowerCase();

		if (newValue !== oldValue) {
			this.typedValue = this.args.value || '';
		}
	}

	private triggerChange() {
		if (isAlive(this)) {
			const value = (this.typedValue || '').toLowerCase();

			if (value === this.args.value) {
				return;
			}

			this.args.onChange(value);
		}
	}

	private scheduleCheckIsActive() {
		once(this, this.checkIsActive);
	}

	<template>
		<div
			class={{joinClasses
				(array
					"input-search"
					this.themeClass
					this.sizeClass
					(if this.isFocused "focused")
					(if this.isActive "active")
					(if this.isClearTextBtnShown "btn-search-clear-visible")
				)
			}}
			{{didUpdate this.onValueChange @value}}
		>
			<CommonUiIconComponent @name="Search" @size="md" @class="icon" />
			{{! template-lint-disable no-passed-in-event-handlers }}
			<FormInputComponent
				name={{this.name}}
				@value={{this.typedValue}}
				maxlength={{this.maxLength}}
				placeholder={{this.placeholder}}
				@input={{this.onInput}}
				@focusIn={{this.focusSearch}}
				@focusOut={{this.unfocusSearch}}
				@insertNewline={{this.onEnterPress}}
				@hideFloatLabel={{true}}
				@autofocus={{@autofocus}}
				@theme={{@theme}}
				@size={{@size}}
			/>
			{{#if this.isClearTextBtnShown}}
				<a role="button" class="btn-search-clear" {{on "click" this.clearText}}>
					<CommonUiIconComponent @name="Close" @size="sm" />
				</a>
			{{/if}}
		</div>
	</template>
}
